import React, { useState } from 'react';
import { useMemo } from "react";
import { useSnapshot } from "valtio";
import { addToCart, saveGeneralInformation, wizardStore } from '../../stores/wizard';
import { Steps } from '../../constants';
import { isEmpty } from '../../utils/general';
import { IMAGE_SERVER } from '../../config';
import { CircleData, ContactInfo, GeneralInformation, LeatherIndicators, StepOption } from '../../types/wizard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCartArrowDown, faCheck, faClose, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { getPriceAndDiscount } from '../../types/emblemData';
import { ContactData } from './ContactData';
import { getCustomerData } from '../../api/art';

interface FloatingAddToCartButtonProps {
  onClick: () => void;
  isVisible?: boolean;
}

export const FloatingAddToCartButton: React.FC<FloatingAddToCartButtonProps> = ({
  onClick,
  isVisible = true
}) => {
  if (!isVisible) return null;

  return (
    <button
      onClick={onClick}
      className="fixed bottom-6 right-6 bg-orange-500 hover:bg-orange-600 text-white px-6 py-4 rounded-full shadow-lg hover:shadow-xl 
      outline-none hover:outline-none focus:outline-none border-0 transition-all duration-300 flex items-center gap-2 font-medium z-50 group"
    >
      <FontAwesomeIcon icon={faCartArrowDown} />
      <span className="hidden sm:inline">Add To Cart</span>
    </button>
  );
};

interface OrderSummaryPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ToastState {
  show: boolean;
  message: string;
  type: 'success' | 'error';
}

export const OrderSummaryPanel: React.FC<OrderSummaryPanelProps> = ({
  isOpen,
  onClose
}) => {
  const wizSnap = useSnapshot(wizardStore);
  const [toast, setToast] = useState<ToastState>({
    show: false,
    message: '',
    type: 'success'
  });

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast(prev => ({ ...prev, show: false }));
    }, 4000);
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, show: false }));
  };

  const handleAddToCartClick = async () => {
    try {
      const customerId = localStorage.getItem("customerId") ?? '';
      const dataToSave: GeneralInformation = {
        requestor: wizSnap.designRequest.generalInformation.requestor || customerId,
        poNumber: wizSnap.designRequest.generalInformation.poNumber || '',
        description: wizSnap.designRequest.generalInformation.description || '',
      };

      // Contact Data
      const defaultResult = await getCustomerData(customerId);
      const defaultContact = defaultResult.success ? defaultResult.data : null;
      const customContact = wizSnap.contactData;

      const effectiveContact: ContactInfo = {
        email: customContact?.email || defaultContact?.email || '',
        fullName: customContact?.fullName || defaultContact?.fullName || '',
        phone: customContact?.phone || defaultContact?.phone || ''
      };

      // TODO: Integrate contact data to document when adding to cart
      console.log(effectiveContact)

      await saveGeneralInformation(dataToSave);
      await addToCart();

      showToast('Product successfully added to cart!', 'success');

      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (error) {
      console.error('Error adding to cart:', error);
      showToast('Failed to add product to cart. Please try again.', 'error');
    }
  };

  return (
    <>
      {/* Toast Notification */}
      {toast.show && (
        <div
          className={`fixed top-6 right-6 z-[60] min-w-[350px] max-w-sm ${toast.type === 'success'
            ? 'bg-green-600'
            : 'bg-red-600'
            } rounded-full shadow-lg transform transition-all duration-500 ease-out`}
          style={{
            animation: 'slideIn 0.4s ease-out'
          }}
        >
          <div className="flex items-center px-4 py-3">
            <div className="flex-shrink-0 w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <FontAwesomeIcon
                icon={toast.type === 'success' ? faCheck : faExclamationTriangle}
                className="w-4 h-4 text-white"
              />
            </div>
            <div className="ml-3 flex-1">
              <p className="text-white font-medium text-sm leading-tight">
                {toast.type === 'success'
                  ? 'Product Added Successfully'
                  : 'Failed to Add Product'
                }
              </p>
              <p className="text-white text-xs opacity-90 mt-0.5">
                {toast.type === 'success'
                  ? 'Your item has been added to cart.'
                  : 'Please try again later.'
                }
              </p>
            </div>
            <button
              onClick={hideToast}
              className="flex-shrink-0 ml-3 w-6 h-6 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-all flex items-center justify-center outline-none hover:outline-none focus:outline-none border-0"
            >
              <FontAwesomeIcon icon={faClose} className="w-3 h-3 text-white" />
            </button>
          </div>
        </div>
      )}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `
      }} />

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      <div className={`fixed top-0 right-0 h-full w-full md:w-[28rem] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'} overflow-y-auto`}>

        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 pl-6 pr-4 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold uppercase">Order Summary</h2>
          <button
            onClick={onClose}
            className="w-[2.5em] p-2 hover:bg-gray-100 transition-colors outline-none hover:outline-none focus:outline-none border-0"
          >
            <FontAwesomeIcon icon={faClose} />
          </button>
        </div>

        <div className="p-6 max-w-full overflow-x-hidden">
          <OrderSummaryContent />
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white p-6 space-y-3 flex flex-col items-center border-t border-gray-200">
          <button
            onClick={handleAddToCartClick}
            className="items-center w-[12em] bg-orange-500 hover:bg-orange-600 text-white py-3 px-4 rounded-3xl font-medium transition-colors outline-none hover:outline-none focus:outline-none border-0"
          >
            Add to Cart
          </button>
          <button
            onClick={onClose}
            className="w-[12em] bg-[#F5F5F5] hover:bg-gray-200 py-3 px-4 rounded-3xl font-medium transition-colors outline-none hover:outline-none focus:outline-none border-0"
          >
            Continue Editing
          </button>
        </div>
      </div>
    </>
  );
};

export const OrderSummaryContent: React.FC = () => {
  const wizSnap = useSnapshot(wizardStore);
  const [customContact, _] = useState<ContactInfo>({
    email: '',
    fullName: '',
    phone: ''
  });

  const handleContactChange = (contact: ContactInfo) => {
    wizardStore.contactData = contact;
  };

  const designTypeImage = useMemo(() => wizSnap.emblemData?.[Steps.DesignType], [wizSnap.emblemData]);
  const specialInstructions = useMemo(() => wizSnap.designRequest?.createDesign?.specialInstructions, [wizSnap.designRequest]);
  const holographicUploadImage = useMemo(() => wizSnap.emblemData?.[Steps.HolographicUpload], [wizSnap.emblemData]);
  const shape = useMemo(() => wizSnap.emblemData?.[Steps.Shape], [wizSnap.emblemData]);
  const sizeStandard = useMemo(() => wizSnap.emblemData?.[Steps.SizeStandard], [wizSnap.emblemData]);
  const fabricColor = useMemo(() => wizSnap.emblemData?.[Steps.FabricColor], [wizSnap.emblemData]);
  const fabricType = useMemo(() => wizSnap.emblemData?.[Steps.FabricType], [wizSnap.emblemData]);
  const backing = useMemo(() => wizSnap.emblemData?.[Steps.Backing], [wizSnap.emblemData]);
  const borderType = useMemo(() => wizSnap.emblemData?.[Steps.BorderType], [wizSnap.emblemData]);
  const borderColor = useMemo(() => wizSnap.emblemData?.[Steps.BorderColor], [wizSnap.emblemData]);
  const layout = useMemo(() => wizSnap.emblemData?.[Steps.Layout], [wizSnap.emblemData]);
  const colors = useMemo(() => wizSnap.emblemData?.[Steps.ThreadColors], [wizSnap.emblemData]);
  const flipColors = useMemo(() => wizSnap.emblemData?.[Steps.HolographicColors], [wizSnap.emblemData]);
  const bgPattern = useMemo(() => wizSnap.emblemData?.[Steps.BackgroundPattern], [wizSnap.emblemData]);
  const scrolling = useMemo(() => wizSnap.emblemData?.[Steps.Scrolling], [wizSnap.emblemData]);
  const sewingGuideColor = useMemo(() => wizSnap.emblemData?.[Steps.SewingGuideColor], [wizSnap.emblemData]);
  const combination = useMemo(() => wizSnap.emblemData?.[Steps.Combination], [wizSnap.emblemData]);
  const emblemPrintStitch = useMemo(() => wizSnap.emblemData?.[Steps.EmblemPrintStitch], [wizSnap.emblemData]);
  const emblemPrintStitchOption = useMemo(
    () => wizSnap.designRequest?.productOptions?.emblemPrintStitch ?? null,
    [wizSnap.designRequest]
  );
  const emblem3d = useMemo(() => wizSnap.emblemData?.[Steps.Emblem3d], [wizSnap.emblemData]);
  const emblem3dOption = useMemo(
    () => wizSnap.designRequest?.productOptions?.emblem3d ?? null,
    [wizSnap.designRequest]
  );
  const emblemLeather = useMemo(() => wizSnap.emblemData?.[Steps.EmblemLeather], [wizSnap.emblemData]);
  const emblemLeatherOption = useMemo(
    () => wizSnap.designRequest?.productOptions?.emblemLeather ?? null,
    [wizSnap.designRequest]
  );

  const activePrice = useMemo(() => {
    if (!wizSnap.pricing || !sizeStandard || !wizSnap.pricing.quantity) return null;
    return getPriceAndDiscount(wizSnap.pricing, sizeStandard, backing?.name, wizSnap.pricing.quantity);
  }, [wizSnap.pricing, sizeStandard, wizSnap.pricing?.quantity]);

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const qty = parseInt(e.target.value, 10) || 0;
    wizardStore.pricing.quantity = qty;
  };

  const pricingLoaded = !isEmpty(wizSnap.pricing);

  return (
    <div className="space-y-6">
      <div className="flex flex-row items-center justify-between gap-2 bg-[#F5F5F5] rounded-lg p-4">
        <div className="flex gap-4">
          {designTypeImage && (
            <div className="w-20 h-20">
              <img src={designTypeImage.imageSrc} className="h-full w-full rounded-lg object-cover" alt="design-type-img" />
            </div>
          )}
          {holographicUploadImage && (
            <div className="w-20 h-20">
              <img src={holographicUploadImage.imageSrc} className="h-full w-full rounded-lg object-cover" alt="holographic-img" />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          {!pricingLoaded ? (
            <div className="flex flex-col gap-2 animate-pulse">
              <div className="flex gap-2 items-center justify-end">
                <div className="h-4 bg-gray-300 rounded w-16"></div>
                <div className="h-10 bg-gray-300 rounded w-20"></div>
              </div>
              <div className="flex items-start justify-between text-lg gap-2">
                <div className="h-4 bg-gray-300 rounded w-20"></div>
                <div className="h-6 bg-orange-200 rounded w-16"></div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <div className='flex gap-2 items-center justify-end'>
                <label className="font-semibold">Quantity:</label>
                <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden bg-white px-2">
                  <button
                    onClick={() => {
                      const newQty = Math.max(1, (wizSnap.pricing.quantity ?? 1) - 1);
                      wizardStore.pricing.quantity = newQty;
                    }}
                    disabled={!pricingLoaded || (wizSnap.pricing.quantity ?? 0) <= 1}
                    className="rounded-xl px-1 py-1 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border-r border-gray-200 
                    outline-none hover:outline-none focus:outline-none"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                  </button>
                  <input
                    type="number"
                    min="1"
                    disabled={!pricingLoaded}
                    className="w-[3.5em] px-2 py-2 text-center border-0 outline-none focus:ring-0 disabled:bg-gray-50"
                    value={wizSnap.pricing.quantity ?? 0}
                    onChange={handleQuantityChange}
                  />
                  <button
                    onClick={() => {
                      const newQty = (wizSnap.pricing.quantity ?? 0) + 1;
                      wizardStore.pricing.quantity = newQty;
                    }}
                    disabled={!pricingLoaded}
                    className="rounded-xl px-1 py-1 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border-l border-gray-200 
                    outline-none hover:outline-none focus:outline-none"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
              </div>

              {activePrice && (
                <div className="flex flex-col gap-1 text-right">
                  <div className="flex items-center justify-end gap-2 text-sm">
                    <span>Unit Price:</span>
                    <span className="font-medium">${activePrice.price.toFixed(2)}</span>
                  </div>

                  {activePrice.discountPercent > 0 && (
                    <div className="flex items-center justify-end gap-2 text-sm text-green-600">
                      <span>Discount Applied:</span>
                      <span className="font-medium">{activePrice.discountPercent.toFixed(1)}%</span>
                    </div>
                  )}

                  <div className="flex items-center justify-end text-lg gap-2">
                    <span className="font-semibold">Total Price:</span>
                    <span className="font-bold text-orange-500">
                      ${(activePrice.price * (wizSnap.pricing?.quantity ?? 0)).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              {!activePrice && (
                <div className="flex items-center justify-between text-lg gap-2">
                  <span className="font-semibold">Total Price:</span>
                  <span className="text-sm">
                    Calculate pricing...
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Product Details */}
      <div>
        <div className="dashed-title my-4"></div>
        <h3 className="font-semibold mb-3">Details</h3>

        <div className="space-y-2 text-sm">
          <div className="flex justify-between gap-2">
            <span className="">Product Type:</span>
            <span className="font-medium">{wizSnap.emblemData?.[Steps.ProductType]?.name}</span>
          </div>

          <div className="flex justify-between gap-2">
            <span className="">Product Subtype:</span>
            <span className="font-medium">{wizSnap.emblemData?.[Steps.ProductSubtype]?.name}</span>
          </div>

          {shape && (
            <div className="flex justify-between gap-2">
              <span className="">Shape:</span>
              <span className="font-medium">{shape.name}</span>
            </div>
          )}

          {sizeStandard && (
            <div className="flex justify-between gap-2">
              <span className="">Size:</span>
              <span className="font-medium">
                {sizeStandard.code ? `${sizeStandard.name} - ` : null}
                {sizeStandard.width ? `Width: ${sizeStandard.width}"` : null}
                {sizeStandard.height ? `, Height: ${sizeStandard.height}"` : null}
                {sizeStandard.scaleHeight ? " Height: Scale Height" : null}
                {sizeStandard.scaleWidth ? " Width: Scale Width" : null}
              </span>
            </div>
          )}

          {combination && (
            <div className="flex justify-between gap-2 items-center">
              <span className="">Combination:</span>
              <div className="flex items-center gap-2">
                {combination?.image?.url ?
                  <img src={`${IMAGE_SERVER}${combination?.image?.url}`} alt="combination_img" className="rounded-full w-5 h-5 border border-slate-300" />
                  :
                  <div className="rounded-full w-5 h-5 border border-slate-300" style={{ backgroundColor: combination.htmlColor }}></div>
                }
                <span className="font-medium">{combination.name}</span>
              </div>
            </div>
          )}

          {fabricColor && (
            <div className="flex justify-between gap-2 items-center">
              <span className="">Fabric/Media Colour:</span>
              <div className="flex items-center gap-2">
                {fabricColor?.image?.url ?
                  <img src={`${IMAGE_SERVER}${fabricColor?.image?.url}`} alt="fabric_color_img" className="rounded-full w-5 h-5 border border-slate-300" />
                  :
                  <div className="rounded-full w-5 h-5 border border-slate-300" style={{ backgroundColor: fabricColor.htmlColor }}></div>
                }
                <span className="font-medium">{fabricColor.name}</span>
              </div>
            </div>
          )}

          {fabricType && (
            <div className="flex justify-between gap-2">
              <span className="">Fabric Type:</span>
              <span className="font-medium">{fabricType.name}</span>
            </div>
          )}

          {backing && (
            <div className="flex justify-between gap-2">
              <span className="">Backing:</span>
              <span className="font-medium">{backing.name}</span>
            </div>
          )}

          {borderType && (
            <div className="flex justify-between gap-2">
              <span className="">Border Type:</span>
              <span className="font-medium">{borderType.name}</span>
            </div>
          )}

          {borderColor && (
            <div className="flex justify-between gap-2">
              <span className="">Border Colour:</span>
              <span className="font-medium">{borderColor.name}</span>
            </div>
          )}

          {layout && (
            <div className="flex justify-between gap-2">
              <span className="">Layout:</span>
              <span className="font-medium">{layout.name}</span>
            </div>
          )}

          {scrolling && (
            <>
              <div className="flex justify-between gap-2">
                <span className="">Scrolling Font:</span>
                <span className="font-medium">{scrolling.scrollingFontDisplayName}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="">Scrolling Text:</span>
                <span className="font-medium">{scrolling.scrollingText}</span>
              </div>
            </>
          )}

          {sewingGuideColor && (
            <div className="flex justify-between gap-2 items-center">
              <span className="">Sewing Guide Colour:</span>
              <div className="flex items-center gap-2">
                <div
                  className="rounded-full w-5 h-5 border border-slate-300"
                  style={{ ...(sewingGuideColor?.htmlColor ? { backgroundColor: sewingGuideColor.htmlColor } : { backgroundImage: `url('${IMAGE_SERVER}${sewingGuideColor?.image?.url}')` }) }}>
                </div>
                <span className="font-medium">{sewingGuideColor.name}</span>
              </div>
            </div>
          )}

          {bgPattern && (
            <div className="flex justify-between gap-2">
              <span className="">Background Pattern:</span>
              <span className="font-medium">{bgPattern.name}</span>
            </div>
          )}

          {designTypeImage && (
            <div className="flex justify-between gap-2">
              <span className="">Design Type:</span>
              <span className="font-medium">Upload Image</span>
            </div>
          )}

          {specialInstructions && (
            <div className="flex justify-between gap-2">
              <span className="">Special Instructions:</span>
              <span className="font-medium">{specialInstructions}</span>
            </div>
          )}
        </div>
      </div>

      {/* 3D Indications */}
      {emblem3d?.items?.length ? (
        <div className="">
          <h3 className="font-semibold mb-3">3D Indications</h3>
          <div className="space-y-2">
            {emblem3d.items.map((item: CircleData, index: number) => (
              <div key={index} className="text-sm">
                <strong>3D Indication {index + 1}:</strong> {item.dimension}, {item.option?.name}
                {item?.instructions && <>, <strong>{item.instructions}</strong></>}
              </div>
            ))}
          </div>
        </div>
      ) : null}


      {(!emblem3d?.items?.length) &&
        (emblem3d?.allowWEItoChoose || emblem3dOption?.set3DEmblemOption === "AllowWEIToChoose") && (
          <div className="">
            <h3 className="font-semibold mb-3">3D Indications</h3>
            <div className="text-sm">
              Allow Stahls Design Team to Choose
            </div>
          </div>
        )
      }

      {/* Leather Indications */}
      {emblemLeather?.indicators?.length ? (
        <div className="">
          <h3 className="font-semibold mb-3">Leather Indications</h3>
          <div className="space-y-2">
            {emblemLeather.indicators.map((item: LeatherIndicators, index: number) => (
              <div key={index} className="text-sm">
                <strong>Indication {index + 1}:</strong> {item.indicator}
                {item?.instructions && <>, <strong>{item.instructions}</strong></>}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {(!emblemLeather || !emblemLeather?.indicators?.length) &&
        emblemLeatherOption?.setEmblemLeatherOption === "AllowWEIToChoose" && (
          <div className="">
            <h3 className="font-semibold mb-3">Leather Indications</h3>
            <div className="text-sm">
              Allow Stahls Design Team to Choose
            </div>
          </div>
        )
      }

      {/* Print Stitch Indications */}
      {emblemPrintStitch?.items?.length ? (
        <div className="">
          <h3 className="font-semibold mb-3">Print Stitch Indications</h3>
          <div className="space-y-2">
            {emblemPrintStitch.items.map((item: CircleData, index: number) => (
              <div key={index} className="flex items-start justify-between">
                <div className="flex items-start gap-2">
                  <div
                    className="rounded-full w-5 h-5 border border-slate-300 flex-shrink-0 mt-0.5"
                    style={{ ...(item.option?.htmlColor ? { backgroundColor: item.option.htmlColor } : { backgroundImage: `url('${IMAGE_SERVER}${item.option?.image?.url}')` }) }}>
                  </div>
                  <span className="text-sm font-medium">
                    <strong>Indication {index + 1}:</strong> {item.option?.name}
                    {item?.instructions && (
                      <span className="text-gray-500 text-sm ml-1 font-normal">- {item?.instructions}</span>
                    )}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {(!emblemPrintStitch || !emblemPrintStitch?.items?.length) &&
        emblemPrintStitchOption?.setEmblemPrintStitchOption === "AllowWEIToChoose" && (
          <div className="">
            <div className="text-sm">
              <strong className='pr-1'>Print Stitch Indications:</strong> Allow Stahls Design Team to Choose
            </div>
          </div>
        )}

      {/* Selected Colors */}
      {colors?.items?.length ? (
        <div className="">
          <div className="dashed-title my-4"></div>
          <h3 className="font-semibold mb-3">Colors</h3>
          <h4 className="font-semibold text-xs mb-3">{colors.items.length} COLOURS SELECTED</h4>
          <div className="space-y-2">
            {colors.items.map(({ option }: { option: StepOption }) => (
              <div key={option.id} className="flex items-start justify-between">
                <div className="flex items-start gap-2">
                  <div
                    className="rounded-full w-5 h-5 border border-slate-300 flex-shrink-0 mt-0.5"
                    style={{
                      ...(option?.htmlColor
                        ? { backgroundColor: option.htmlColor }
                        : option?.image?.url
                          ? {
                            backgroundImage: `url('${IMAGE_SERVER}${option?.image?.url}')`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                          }
                          : {})
                    }}>
                  </div>
                  <span className="text-sm font-medium">
                    {option.name}
                    {option?.instructions && (
                      <span className="text-gray-500 text-sm ml-1 font-normal">- {option?.instructions}</span>
                    )}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {colors?.allowWEItoChoose && colors?.items?.length === 0 && (
        <div>
          <div className="dashed-title my-4"></div>
          <h3 className="font-semibold mb-3">Colors</h3>
          <div className="text-sm">
            The Stahls Design Team will select the colours for this design.
          </div>
        </div>
      )}

      {/* Flip Image Selected Colors */}
      {flipColors?.items?.length ? (
        <div className="pt-2">
          <div className="dashed-title my-4"></div>
          <h3 className="font-semibold mb-3">Flip Image Colours</h3>
          <h4 className="font-semibold text-xs mb-3">{flipColors.items.length} COLOURS SELECTED</h4>
          <div className="space-y-2">
            {flipColors.items.map(({ option }: { option: StepOption }) => (
              <div key={option.id} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className="rounded-full w-5 h-5 border border-slate-300"
                    style={{ ...(option?.htmlColor ? { backgroundColor: option.htmlColor } : { backgroundImage: `url('${IMAGE_SERVER}${option?.image?.url}')` }) }}>
                  </div>
                  <span className="text-sm font-medium">{option.name}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="dashed-title my-4"></div>

      {/* Contact Data */}
      <ContactData
        currentContact={customContact}
        onContactChange={handleContactChange}
      />

    </div>
  );
};